import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import Modal from 'react-responsive-modal'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { createVaccine, updateVaccine } from '../../actions/medCenter/vassinations'
import { Checkbox, Input, Select, Textarea } from '../../components/ModalInputs'
import Button from '../../components/Button'
import Confirmation from '../../components/Confirmation/Confirmation'
import VaccineTypesModal from './VaccineTypesModal'
import styles from './styles.module.scss'

const INITIAL_VALUES = {
  c_vaccine: 0,
  name: '',
  used: null,
  c_vac_type: 0,
  phase: 1,
  expired: 2,
  pause: 180,
  disabled: 1,
  notes: '',
  expire_unit: 1
}

const EXPIRE_UNITS = [
  { value: 1, label: 'дней' },
  { value: 2, label: 'месяцев' },
  { value: 3, label: 'лет' }
]

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Обязательное поле')
    .min(4, 'Минимум 4 символа')
    .max(50, 'Не более 50 символов'),
  phase: Yup.number()
    .required('Обязательное поле')
    .max(9999, 'Не более 4 цифр'),
  pause: Yup.number()
    .required('Обязательное поле')
    .max(9999, 'Не более 4 цифр'),
  expired: Yup.number()
    .required('Обязательное поле')
    .max(9999, 'Не более 4 цифр'),
  notes: Yup.string()
    .max(255, 'Не более 255 символов')
})

const VaccinationsModal = ({
  open,
  onClose,
  data,
  addMode,
  typesVaccinations
}) => {
  const dispatch = useDispatch()
  const [confirmation, setConfirmation] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showTypesModal, setShowTypesModal] = useState(false)

  const createItem = useCallback(async (values) => {
    setIsSubmitting(true)
    try {
      await dispatch(createVaccine(values))
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, onClose])

  const updateItem = useCallback(async (values) => {
    setIsSubmitting(true)
    try {
      await dispatch(updateVaccine(values))
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, onClose])

  const {
    values,
    errors,
    touched,
    dirty,
    submitCount,
    handleSubmit,
    handleChange,
    handleBlur,
    setValues,
    resetForm
  } = useFormik({
    initialValues: INITIAL_VALUES,
    enableReinitialize: true,
    validationSchema,
    onSubmit: addMode ? createItem : updateItem
  })

  useEffect(() => {
    if (!open) {
      resetForm()
      return
    }
    setValues(addMode ? INITIAL_VALUES : data)
  }, [open, addMode, data, setValues, resetForm])

  const handleCloseModal = useCallback(() => {
    if (dirty) {
      setConfirmation(true)
    } else {
      onClose()
    }
  }, [dirty, onClose])

  const acceptConfirmation = useCallback(() => {
    onClose()
    setConfirmation(false)
  }, [onClose])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(false)
  }, [])

  const getError = useCallback((fieldName) =>
    (touched[fieldName] || submitCount > 0) && errors[fieldName],
    [touched, errors, submitCount]
  )

  const typeOptions = useMemo(() =>
    typesVaccinations?.map(({ name, c_vac_type }) => (
      <option key={c_vac_type} value={c_vac_type}>{name}</option>
    )) || [],
    [typesVaccinations]
  )

  const expireUnitOptions = useMemo(() =>
    EXPIRE_UNITS.map(({ value, label }) => (
      <option key={value} value={value}>{label}</option>
    )),
    []
  )

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      classNames={{ modal: styles.modal }}
      closeOnEsc={!dirty}
      closeOnOverlayClick={!dirty}
      showCloseIcon={false}
      animationDuration={200}
      center
    >
      <form onSubmit={handleSubmit} className={styles.modalForm}>
        <div className={styles.modalMain}>
          <h2>{addMode ? 'Добавление прививки' : 'Редактирование прививки'}</h2>

          <div className={styles.formRow}>
            <Select
              label="Тип вакцины"
              name="c_vac_type"
              value={values.c_vac_type || 0}
              error={getError('c_vac_type')}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSubmitting}
            >
              <option value={0}>Выберите тип</option>
              {typeOptions}
            </Select>
            <Button
              type="button"
              mode="white"
              onClick={() => setShowTypesModal(true)}
              disabled={isSubmitting}
              style={{ marginTop: '1.5rem' }}
            >
              Изменить
            </Button>
          </div>

          <Input
            label="Наименование"
            name="name"
            value={values.name || ''}
            error={getError('name')}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting}
          />

          <div className={styles.formRow}>
            <Input
              label="Компонент"
              name="phase"
              type="number"
              value={values.phase ?? ''}
              error={getError('phase')}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSubmitting}
            />
            <Input
              label="Срок действия"
              name="pause"
              type="number"
              value={values.pause ?? ''}
              error={getError('pause')}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSubmitting}
            />
            <Select
              label="Исчисление"
              name="expire_unit"
              value={values.expire_unit || 1}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSubmitting}
            >
              {expireUnitOptions}
            </Select>
            <Input
              label="Интервал"
              name="expired"
              type="number"
              value={values.expired ?? ''}
              error={getError('expired')}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.formRow}>
            <Checkbox
              label="Обучающийся"
              name="disabled"
              checked={!!values.disabled}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            <Checkbox
              label="Сотрудник"
              name="used"
              checked={!!values.used}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>

          <Textarea
            label="Описание"
            name="notes"
            value={values.notes || ''}
            error={getError('notes')}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting}
            style={{ minHeight: '100px', resize: 'vertical' }}
          />
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.footerInfo} />
          <div className={styles.footerButtons}>
            <Button
              mode="white"
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting || !dirty}
            >
              Сохранить
            </Button>
            <Button
              mode="white"
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Закрыть
            </Button>
          </div>
        </div>
      </form>

      {confirmation && (
        <Confirmation
          title="Есть несохраненные данные. Уверены что хотите выйти?"
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
        />
      )}

      {showTypesModal && (
        <VaccineTypesModal
          onClose={() => setShowTypesModal(false)}
          typesVaccinations={typesVaccinations}
        />
      )}
    </Modal>
  )
}

export default VaccinationsModal
